import { Button } from '@/components/ui/button';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { CTA_ACTION_TYPE, CTA_TYPE } from '@/constants/bot';
import { getLinksForCTA } from '@/services/bots-refactor';
import { ILinkForCTA } from '@/types/bots';
import { IPageCTAForm } from '@/types/page-cta';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';

interface IAISuggestionsPanelProps {
  chatToken: string;
}

const AISuggestionsPanel: React.FC<IAISuggestionsPanelProps> = ({ chatToken }) => {
  const form = useFormContext<IPageCTAForm>();
  const urls = form.watch('urls') || [];
  const url = form.watch('url') || '';

  const targetUrl = urls.length === 1 ? urls[0] : (urls.length === 0 && url ? url : null);

  const { data, isFetching } = useQuery({
    queryKey: [API_ENDPOINTS.getLinks, chatToken, targetUrl, 'ai-suggestions'],
    queryFn: () => getLinksForCTA({
      chat_token: chatToken,
      page: 1,
      limit: 5,
      searchText: targetUrl,
    }),
    enabled: !!chatToken && !!targetUrl,
    staleTime: 1000 * 60,
  });

  if (!targetUrl) return null;

  const link: ILinkForCTA | undefined = data?.data?.find((l) => l.link === targetUrl) || data?.data?.[0];
  const welcomes = Array.isArray(link?.ai_welcome_suggestions) ? link?.ai_welcome_suggestions ?? [] : [];
  const prompts = Array.isArray(link?.ai_prompt_suggestions) ? link?.ai_prompt_suggestions ?? [] : [];

  if (isFetching) {
    return (
      <div className='ai-suggestions text-s text-black-200'>Loading AI suggestions…</div>
    );
  }

  if (welcomes.length === 0 && prompts.length === 0) {
    return null;
  }

  const applyWelcome = (text: string) => {
    form.setValue('popup_headline', text, { shouldDirty: true });
  };

  const applyPrompt = (text: string, slot: 'option_a' | 'option_b' | 'option_c') => {
    // PageCTAs.onSubmit drops a slot when its `type` is falsy, so set both the label
    // and a default action shape (open_chatbot) — that matches what handlePageVisit
    // emits for vendor-configured CTAs.
    const existing = form.getValues(slot);
    form.setValue(slot, {
      ...(existing || {}),
      label: text,
      type: existing?.type || CTA_TYPE.ACTION,
      config: {
        ...(existing?.config || {}),
        action_type: existing?.config?.action_type || CTA_ACTION_TYPE.OPEN_CHATBOT,
      },
    }, { shouldDirty: true });
  };

  return (
    <div className='ai-suggestions border border-border rounded-md p-4 flex flex-col gap-4 bg-slate-50'>
      <div>
        <p className='text-sm font-medium text-black mb-0.5'>AI-suggested based on your page content</p>
        <p className='text-s text-black-200'>
          Auto-generated for <span className='font-medium'>{targetUrl}</span>. Promote a suggestion to use it as the popup headline or a CTA label.
        </p>
      </div>

      {welcomes.length > 0 ? (
        <div>
          <p className='text-s font-medium text-black mb-2'>Welcome messages</p>
          <ul className='flex flex-col gap-2'>
            {welcomes.map((w, i) => (
              <li key={`w-${i}`} className='flex items-start justify-between gap-3 p-2 bg-white border border-border rounded'>
                <span className='text-s text-black flex-1'>{w}</span>
                <Button type='button' variant='outline' size='sm' onClick={() => applyWelcome(w)}>
                  Use as headline
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {prompts.length > 0 ? (
        <div>
          <p className='text-s font-medium text-black mb-2'>Prompt suggestions</p>
          <ul className='flex flex-col gap-2'>
            {prompts.map((p, i) => (
              <li key={`p-${i}`} className='flex items-start justify-between gap-3 p-2 bg-white border border-border rounded'>
                <span className='text-s text-black flex-1'>{p}</span>
                <div className='flex gap-1'>
                  <Button type='button' variant='outline' size='sm' onClick={() => applyPrompt(p, 'option_a')}>A</Button>
                  <Button type='button' variant='outline' size='sm' onClick={() => applyPrompt(p, 'option_b')}>B</Button>
                  <Button type='button' variant='outline' size='sm' onClick={() => applyPrompt(p, 'option_c')}>C</Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default AISuggestionsPanel;
